import {
  buildFormSchemaFromFields,
  filterFieldsByQuestionLogic,
  packAnswerPayload,
  type QuizQuestion,
} from "@core/features/quiz";
import {
  CHOICE_KINDS,
  DESIGNED_KINDS,
  publicQuizQuestionsFrom,
  type PublicQuizQuestionKind,
  type PublicQuizQuestionType,
} from "@platform/features/programQuiz";

/**
 * A form's own questions, as the intake screens draw them.
 *
 * The built-in fields mean the same thing on every form, so they have columns
 * and components of their own. Everything else a form asks is a question of
 * its quiz: a funder is asked what they want to explore, an organization how
 * many people it could invite. They are configured as quiz questions and are
 * turned into form fields, validated, shown or hidden, and packed for the
 * server by the same code that does those things for every other quiz, and
 * drawn by the renderer every quiz shown to the public is drawn by
 * (PublicQuizQuestion). What is added here is only what a quiz does not have:
 * which of the form's screens a question is on.
 */
export type IntakeQuestionKind = Exclude<PublicQuizQuestionKind, "statement" | "other">;

export type IntakeQuestion = PublicQuizQuestionType & {
  kind: IntakeQuestionKind;
  /** Which step of the form this question appears on */
  step: number;
};

/** What somebody has answered so far, keyed by question name. */
export type IntakeAnswers = Record<string, unknown>;

/** The longest written answer the server keeps. */
const MAX_ANSWER_LENGTH = 2000;

/**
 * Turn a form's quiz questions into the questions its screens draw. The intake
 * screens draw the six kinds the design gives; a question of any other kind
 * (an upload, a slider, a statement agreed to by going on) is left out of the
 * form.
 */
export function intakeQuestionsFrom(questions: QuizQuestion[] = []): IntakeQuestion[] {
  return publicQuizQuestionsFrom(questions).flatMap((question) =>
    DESIGNED_KINDS.includes(question.kind)
      ? [
          {
            ...question,
            kind: question.kind as IntakeQuestionKind,
            step: question.question.config_object?.intake_step ?? 1,
          },
        ]
      : []
  );
}

/** Every question unanswered, in the shape its input expects. */
export function emptyAnswers(questions: IntakeQuestion[]): IntakeAnswers {
  return Object.fromEntries(
    questions.map((question) => [
      question.name,
      CHOICE_KINDS.includes(question.kind) ? [] : "",
    ])
  );
}

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
/** A host with a dot in it, and whatever follows: "example.org", "example.org/work". */
const LOOKS_LIKE_AN_ADDRESS = /^[^\s/?#@]+\.[a-z0-9-]{2,}(?:[:/?#]\S*)?$/i;

/**
 * Whether what was typed where a web address is asked for is words instead.
 *
 * Two of the forms ask for a "website or mission", so a sentence is a fair
 * answer there, and anybody may write "none yet" where a portfolio is asked
 * for. Words are kept as they were typed. They are not given a scheme, which
 * made a link out of a sentence, and they are not held to the rule for
 * addresses, which would refuse an answer the question invited.
 */
function isWords(text: string) {
  return !HAS_SCHEME.test(text) && !LOOKS_LIKE_AN_ADDRESS.test(text);
}

/**
 * The answers as they will be checked and sent.
 *
 * Written answers are trimmed, and one left empty is no answer at all. A web
 * address typed without its scheme is given one, since "example.org" is how
 * people write an address and the quiz insists on a full one. Words that are
 * not an address are left as words: see `isWords`.
 */
function tidy(questions: IntakeQuestion[], answers: IntakeAnswers = {}): IntakeAnswers {
  const tidied: IntakeAnswers = {};

  questions.forEach((question) => {
    const value = answers[question.name];

    if (CHOICE_KINDS.includes(question.kind)) {
      // The choice components hand back an array whatever the kind.
      const chosen = Array.isArray(value) ? value : value == null ? [] : [value];
      tidied[question.name] = chosen.filter((id): id is number => typeof id === "number");
      return;
    }

    let text = typeof value === "string" ? value.trim() : "";
    if (text && question.kind === "url" && !HAS_SCHEME.test(text) && !isWords(text)) {
      text = `https://${text}`;
    }
    if (text) tidied[question.name] = text;
  });

  return tidied;
}

function isAnswered(value: unknown) {
  return Array.isArray(value) ? value.length > 0 : !!value;
}

/**
 * The questions on screen, given what has been answered.
 *
 * A question may be configured to appear only after a particular answer to
 * another. One that is hidden is neither required nor sent.
 */
export function shownQuestions(
  questions: IntakeQuestion[],
  answers: IntakeAnswers = {}
): IntakeQuestion[] {
  const shown = new Set(
    filterFieldsByQuestionLogic(
      questions.map((question) => question.field),
      tidy(questions, answers)
    ).map((field) => field.name)
  );
  return questions.filter((question) => shown.has(question.name));
}

/**
 * The questions to draw, given what has been answered, each saying whether it
 * can be answered yet.
 *
 * Those on screen can. A conditional question configured to wait in view
 * (`intake_when_not_asked: "disabled"`) is drawn while its condition is unmet
 * too, disabled: a State question that can be seen to wait on the country.
 * Neither checked nor sent while it waits, it is otherwise as if hidden.
 */
export function drawnQuestions(
  questions: IntakeQuestion[],
  answers: IntakeAnswers = {}
): { question: IntakeQuestion; isWaiting: boolean }[] {
  const shown = new Set(shownQuestions(questions, answers).map((question) => question.name));
  return questions
    .filter(
      (question) =>
        shown.has(question.name) ||
        question.question.config_object?.intake_when_not_asked === "disabled"
    )
    .map((question) => ({ question, isWaiting: !shown.has(question.name) }));
}

/** What is wrong with the answers, if anything: one message per question. */
export function answerIssues(
  questions: IntakeQuestion[],
  answers: IntakeAnswers = {}
): { name: string; message: string }[] {
  const tidied = tidy(questions, answers);
  const issues: { name: string; message: string }[] = [];
  const toCheck: IntakeQuestion[] = [];

  shownQuestions(questions, answers).forEach((question) => {
    const value = tidied[question.name];
    if (!isAnswered(value)) {
      if (question.is_required) {
        issues.push({ name: question.name, message: "This answer is required" });
      }
      return;
    }
    if (typeof value === "string" && value.length > MAX_ANSWER_LENGTH) {
      issues.push({
        name: question.name,
        message: `Must be ${MAX_ANSWER_LENGTH} characters or less`,
      });
      return;
    }
    // Words given where an address is asked for are an answer as they stand.
    if (question.kind === "url" && typeof value === "string" && isWords(value)) return;
    toCheck.push(question);
  });

  // Whatever was answered is held to the quiz's own rules for it.
  const result = buildFormSchemaFromFields(toCheck.map((question) => question.field)).safeParse(
    Object.fromEntries(toCheck.map((question) => [question.name, tidied[question.name]]))
  );
  if (!result.success) {
    const seen = new Set(issues.map((issue) => issue.name));
    result.error.issues.forEach((issue) => {
      const name = String(issue.path[0]);
      if (seen.has(name)) return;
      seen.add(name);
      issues.push({ name, message: issue.message });
    });
  }

  return issues;
}

/**
 * The answers as the server takes a quiz's: one entry per answered question
 * that was on screen, naming the question and carrying its text or its chosen
 * options.
 */
export function packAnswers(questions: IntakeQuestion[], answers: IntakeAnswers = {}) {
  const tidied = tidy(questions, answers);

  return shownQuestions(questions, answers)
    .filter((question) => isAnswered(tidied[question.name]))
    .map((question) =>
      packAnswerPayload({
        question: question.question,
        answer: tidied[question.name] as number[] | string,
      })
    );
}
